import { useLazyQuery, useQuery } from '@apollo/client';
import { AccountClient } from '@nile/graphql';
import { GET_NOTIFICATION_TEMPLATES } from '@nile/banyan/src/Graphql/Mutations/templates';
import { TEMPLATE_TYPES } from '@nile/banyan/src/Constants/notifications';
import {
  FieldTitle,
  Loader,
  COMMON_CONSTANTS,
  toast,
  NoDataCard
} from '@nile/components';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { DisplayText } from '@superops/ui-design-system';
import { GET_DEFAULT_SIDE_CONVERSATION_REPLY_TEMPLATE } from '@nile/graphql/src/Query/ticket';
import { isEmpty } from 'lodash';
import envConfig from '@nile/home/src/Config/env';

interface SideConversationListProps {
  toggleSheetActive: () => void;
  populateTemplate: (template: any) => void;
  notificationTemplateType?: string;
  workItemId?: string;
  module?: string;
  selectedMailBox?: any;
}

const SideConversationList: React.FC<SideConversationListProps> = ({
  toggleSheetActive,
  populateTemplate,
  notificationTemplateType = TEMPLATE_TYPES.sideConversation,
  workItemId,
  module,
  selectedMailBox
}) => {
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(
    null
  );

  const { loading, error, data } = useQuery(GET_NOTIFICATION_TEMPLATES, {
    client: AccountClient,
    variables: { notificationTemplateType },
    fetchPolicy: 'network-only'
  });

  const [getTemplateContent] = useLazyQuery(
    GET_DEFAULT_SIDE_CONVERSATION_REPLY_TEMPLATE,
    {
      client: AccountClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      onCompleted: (response) => {
        const getVendorConversationContentFromTemplate =
          response?.getVendorConversationContentFromTemplate;

        if (getVendorConversationContentFromTemplate) {
          toggleSheetActive();
          populateTemplate(getVendorConversationContentFromTemplate);
        }
        setLoadingTemplateId(null);
      },
      onError: (err) => {
        toast('error', err.message);
        setLoadingTemplateId(null);
      }
    }
  );

  if (loading)
    return (
      <div className="center fh flex w-300 tw-p-4">
        <Loader />
      </div>
    );
  if (error) {
    return (
      <div className="tw-p-2">
        <FormattedMessage id="agentMarketplace.error.message" />
      </div>
    );
  }

  const populateTemplateContent = (template: any) => {
    if (!isEmpty(workItemId) && !isEmpty(module)) {
      setLoadingTemplateId(template.id);
      const variables = {
        input: {
          template: {
            templateId: template.id
          },
          workItem: {
            workId: workItemId,
            module: module
          },
          mailbox: selectedMailBox?.boxId
            ? {
                boxId: selectedMailBox.boxId
              }
            : null
        }
      };
      getTemplateContent({ variables });
    } else {
      // Fallback to direct template population if workItemId or module is not available
      toggleSheetActive();
      populateTemplate(template);
    }
  };

  return (
    <div className="side-conversation-list-wrapper vw30 vh100 tw-p-2 scroll  bg-white">
      <div className="side-conversation-header tw-mb-2 tw-p-2">
        <DisplayText
          element="h2"
          size="extraLarge"
          variant="default"
          weight="SemiBold"
        >
          <FormattedMessage id="salesInbox.listView.sideConversations" />
        </DisplayText>
      </div>
      {data?.getNotificationTemplates?.length === 0 ? (
        <div className="center fh flex fw jcenter">
          <NoDataCard
            imageURL={`url(${envConfig.imageRoot}svg/documents-empty-state.svg)`}
            text={<FormattedMessage id={'common.noData'} />}
            type="secondary"
          />
        </div>
      ) : null}
      {data?.getNotificationTemplates?.map((el: any) => {
        const isLoading = loadingTemplateId === el.id;
        return (
          <div
            key={el.id}
            className="side-conversation-template tw-mb-2 tw-p-2 ptr br8"
            onClick={() => !isLoading && populateTemplateContent(el)}
            role="button"
            tabIndex={0}
            style={{
              opacity: isLoading ? 0.6 : 1,
              pointerEvents: isLoading ? 'none' : 'auto'
            }}
          >
            <div className="flex center justify-between">
              <FieldTitle>{el.name}</FieldTitle>
              {isLoading && (
                <div className="tw-ml-2">
                  <Loader />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SideConversationList;
